import { Trans } from '@lingui/macro'

import * as React from 'react'

import { ButtonLarge } from '~ui/components/buttons/ButtonSizes/ButtonLarge'
import { ButtonWithBack } from '~ui/components/buttons/ButtonWithBack/ButtonWithBack'
import { Icon } from '~ui/components/icons/Icon'
import { IconCart } from '~ui/components/icons/specific/IconCart'
import { IconClose } from '~ui/components/icons/specific/IconX'
import { GridRow } from '~ui/components/layout/Box'
import { Text } from '~ui/components/typography/Text/Text'

import { useCartItemSubmitCtx } from '../../Cart'
import { useProductDetailControlCtx, useProductDetailCtx } from '../contexts/ProductDetailProvider'
import { useProductDetailStateCtx } from '../contexts/ProductDetailStateProvider'

type TProps = NoChildren

type TCanCreateOrder = {
  canCreateOrder?: boolean
}

type TMobileProps = TCanCreateOrder & {
  closeMobileProductDetail: () => void
}

/**
 * Button for submitting & closing the product detail.
 * Includes a back/close button – used on mobile.
 */
export const SubmitCartItemButtonWithBack: React.FC<TProps & TMobileProps> = ({
  canCreateOrder = true,
  closeMobileProductDetail,
}) => {
  // read-only menu, a customer can view the product detail but he/she can't add it to the cart
  // submit button in that case still shows the price, but instead of submitting (adding to the cart) it just closes the detail

  const { product } = useProductDetailCtx()
  const { canSubmit, productPriceSum } = useProductDetailStateCtx()
  const { submitCartItem, showValidationHint } = useCartItemSubmitCtx()

  const canAddToCart = canCreateOrder && !product?.isSoldOut

  const bgColor: PropsOf<typeof ButtonWithBack>['bgColor'] = canAddToCart
    ? `conversion`
    : `secondary`

  const submitAndClose = () => {
    submitCartItem()
    closeMobileProductDetail()
  }

  const getOnClick = () => {
    if (!canAddToCart) {
      return closeMobileProductDetail
    }

    if (!canSubmit) {
      return showValidationHint
    }

    return submitAndClose
  }

  return (
    <ButtonWithBack
      buttonSize={`large`}
      bgColor={bgColor}
      onBackClick={closeMobileProductDetail}
      onClick={getOnClick()}
    >
      <GridRow
        flexGrow={1}
        columnGap={`small`}
        placeItems={`center`}
        justifyContent={`center`}
        gridTemplateColumns={`auto auto`}
      >
        <Icon icon={canAddToCart ? IconCart : IconClose} iconSize={`1.25rem`} />
        <Text fontSize={`m18`} fontStyle={`boldSemi`}>
          {productPriceSum}
        </Text>
      </GridRow>
    </ButtonWithBack>
  )
}

/**
 * button for submitting & closing the product detail
 * used on tablet/desktop
 */
export const SubmitCartItemButton: React.FC<TProps & TCanCreateOrder> = ({
  canCreateOrder = true,
}) => {
  const { isEditingItem, product } = useProductDetailCtx()
  const { closeProductDetail } = useProductDetailControlCtx()
  const { canSubmit, isLoading } = useProductDetailStateCtx()
  const { submitCartItem, showValidationHint } = useCartItemSubmitCtx()

  // read-only menu OR sold out product, a customer can view the product detail but he/she can't add it to the cart
  if (!canCreateOrder || product?.isSoldOut) {
    return null
  }

  const submitAndClose = () => {
    submitCartItem()
    closeProductDetail()
  }

  return (
    <ButtonLarge
      bgColor={`conversion`}
      state={isLoading ? `loading` : `enabled`}
      onClick={canSubmit ? submitAndClose : showValidationHint}
    >
      {isEditingItem ? <Trans>Confirm changes</Trans> : <Trans>Add to cart</Trans>}
    </ButtonLarge>
  )
}
